import React, { useState } from 'react';
import SectionTitle from '../components/ui/SectionTitle';

interface FormData {
  lifeSituation: 'single' | 'partnership' | 'family' | '';
  recentChanges: 'Ja' | 'Nein' | 'Unsicher' | '';
  changeDetails: string;
  hasLiabilityInsurance: 'Ja' | 'Nein' | 'Unsicher' | '';
  hasHealthInsurance: 'Ja' | 'Nein' | 'Unsicher' | '';
  hasIncomeProtection: 'Ja' | 'Nein' | 'Unsicher' | '';
  hasDeathCoverage: 'Ja' | 'Nein' | 'Unsicher' | '';
  hasRetirementPlan: 'Ja' | 'Nein' | 'Unsicher' | '';
  hasInsuranceOverview: 'Ja' | 'Nein' | 'Unsicher' | '';
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  additionalInfo: string;
  consent: boolean;
}

const SelbstCheck: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    lifeSituation: '',
    recentChanges: '',
    changeDetails: '',
    hasLiabilityInsurance: '',
    hasHealthInsurance: '',
    hasIncomeProtection: '',
    hasDeathCoverage: '',
    hasRetirementPlan: '',
    hasInsuranceOverview: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    additionalInfo: '',
    consent: false,
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const encode = (data: Record<string, string>) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const calculateScore = () => {
    const questions = [
      'hasLiabilityInsurance',
      'hasHealthInsurance',
      'hasIncomeProtection',
      'hasDeathCoverage',
      'hasRetirementPlan',
      'hasInsuranceOverview'
    ];

    return questions.reduce((score, question) => {
      return score + (formData[question as keyof FormData] === 'Ja' ? 1 : 0);
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "selbst-check",
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          lifeSituation: formData.lifeSituation,
          recentChanges: formData.recentChanges,
          changeDetails: formData.changeDetails,
          hasLiabilityInsurance: formData.hasLiabilityInsurance,
          hasHealthInsurance: formData.hasHealthInsurance,
          hasIncomeProtection: formData.hasIncomeProtection,
          hasDeathCoverage: formData.hasDeathCoverage,
          hasRetirementPlan: formData.hasRetirementPlan,
          hasInsuranceOverview: formData.hasInsuranceOverview,
          insuranceScore: String(calculateScore()),
          additionalInfo: formData.additionalInfo,
          consent: formData.consent ? 'Ja' : 'Nein'
        })
      });

      setSubmitted(true);
      setFormData({
        lifeSituation: '',
        recentChanges: '',
        changeDetails: '',
        hasLiabilityInsurance: '',
        hasHealthInsurance: '',
        hasIncomeProtection: '',
        hasDeathCoverage: '',
        hasRetirementPlan: '',
        hasInsuranceOverview: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        additionalInfo: '',
        consent: false,
      });

      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Fehler beim Senden des Formulars. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt.');
      
      setTimeout(() => {
        setError(null);
      }, 8000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const insuranceQuestions = [
    {
      id: 'hasLiabilityInsurance',
      question: 'Haben Sie eine Privathaftpflichtversicherung?'
    },
    {
      id: 'hasHealthInsurance',
      question: 'Entspricht Ihre Krankenversicherung Ihren aktuellen Bedürfnissen?'
    },
    {
      id: 'hasIncomeProtection',
      question: 'Ist Ihr Einkommen bei längerer Krankheit oder Berufsunfähigkeit geschützt?'
    },
    {
      id: 'hasDeathCoverage',
      question: 'Sind Ihre Angehörigen im Todesfall finanziell abgesichert?'
    },
    {
      id: 'hasRetirementPlan',
      question: 'Haben Sie bereits für Ihren Ruhestand vorgesorgt?'
    },
    {
      id: 'hasInsuranceOverview',
      question: 'Haben Sie einen klaren Überblick über Ihre bestehenden Versicherungen?'
    }
  ];

  return (
    <>
      <section className="pt-32 pb-16 bg-gray-light">
        <div className="container-custom">
          <h1 className="font-bold mb-4">Versicherungs-Selbstcheck</h1>
          <p className="text-xl text-gray-600">
            In nur 3 Minuten erhalten Sie eine erste Einschätzung Ihrer Absicherung – kostenfrei und unverbindlich.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-custom max-w-3xl">
          <form 
            name="selbst-check" 
            method="POST" 
            data-netlify="true" 
            netlify-honeypot="bot-field"
            onSubmit={handleSubmit} 
            className="space-y-8"
          >
            <input type="hidden" name="form-name" value="selbst-check" />
            <input type="hidden" name="insuranceScore" value={String(calculateScore())} />
            
            {/* Honeypot field */}
            <p className="hidden">
              <label>
                Don't fill this out: <input name="bot-field" onChange={() => {}} />
              </label>
            </p>

            {submitted && (
              <div className="bg-green-100 text-green-800 p-4 rounded-md mb-6">
                Vielen Dank für Ihre Anfrage. Wir melden uns zeitnah bei Ihnen.
              </div>
            )}

            {error && (
              <div className="bg-red-100 text-red-800 p-4 rounded-md mb-6">
                {error}
              </div>
            )}

            <div className="space-y-6">
              <SectionTitle title="Teil 1 – Ihre persönliche Situation" />
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <p className="font-medium mb-4">Wie ist Ihre aktuelle Lebenssituation?</p>
                <div className="space-x-6">
                  {[
                    { value: 'single', label: 'Single' },
                    { value: 'partnership', label: 'Partnerschaft' },
                    { value: 'family', label: 'Familie' }
                  ].map(({ value, label }) => (
                    <label key={value} className="inline-flex items-center">
                      <input
                        type="radio"
                        name="lifeSituation"
                        value={value}
                        checked={formData.lifeSituation === value}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <p className="font-medium mb-4">Gab es kürzlich wichtige Veränderungen in Ihrem Leben?</p>
                <div className="space-x-6 mb-4">
                  {['Ja', 'Nein', 'Unsicher'].map((option) => (
                    <label key={option} className="inline-flex items-center">
                      <input
                        type="radio"
                        name="recentChanges"
                        value={option}
                        checked={formData.recentChanges === option}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      {option}
                    </label>
                  ))}
                </div>
                
                {formData.recentChanges === 'Ja' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Welche Veränderungen haben stattgefunden?
                    </label>
                    <input
                      type="text"
                      name="changeDetails"
                      value={formData.changeDetails}
                      onChange={handleChange}
                      placeholder="z.B. Umzug, Familienzuwachs, neuer Job"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <SectionTitle title="Teil 2 – Ihr Versicherungsschutz" />
              
              {insuranceQuestions.map((q) => (
                <div key={q.id} className="bg-white p-6 rounded-lg shadow-sm">
                  <p className="font-medium mb-3">{q.question}</p>
                  <div className="space-x-6">
                    {['Ja', 'Nein', 'Unsicher'].map((option) => (
                      <label key={option} className="inline-flex items-center">
                        <input
                          type="radio"
                          name={q.id}
                          value={option}
                          checked={formData[q.id as keyof FormData] === option}
                          onChange={handleChange}
                          className="mr-2"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              <SectionTitle title="Ihre Kontaktdaten" />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vorname
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nachname
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-Mail-Adresse
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefonnummer
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Was ist Ihnen bei Ihrer Absicherung besonders wichtig?
                </label>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <input
                type="checkbox"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                required
                className="mt-1"
              />
              <label className="text-sm text-gray-600">
                Ich stimme der Verarbeitung meiner Daten gemäß Datenschutzbestimmungen zu.
              </label>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={!formData.consent || isSubmitting}
              >
                {isSubmitting ? 'Wird gesendet...' : 'Selbst-Check absenden'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};

export default SelbstCheck;